import React, { useEffect, useRef, useState } from 'react';
import { Users, Cpu, Leaf, CheckCircle, Briefcase, Award, TrendingUp, Headphones } from 'lucide-react';

const iconMap = {
  users: Users,
  cpu: Cpu,
  leaf: Leaf,
  'check-circle': CheckCircle,
  briefcase: Briefcase,
  award: Award,
  'trending-up': TrendingUp,
  headphones: Headphones
};

const AnimatedCounter = ({ value, isVisible }) => {
  const [count, setCount] = useState('0');
  const hasNumber = /\d/.test(value);

  useEffect(() => {
    if (!isVisible || !hasNumber) {
      setCount(value);
      return;
    }

    const numericValue = parseInt(value.replace(/\D/g, ''));
    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericValue) {
        setCount(value);
        clearInterval(timer);
      } else {
        const suffix = value.replace(/[\d.]/g, '');
        setCount(Math.floor(current) + suffix);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value, hasNumber]);

  return <span>{count}</span>;
};

export const WhyNorthBridge = ({ stats, reasons }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="why-section">
      <div className="container">
        <h2 className="section-title">Why Choose North Bridge</h2>
        
        <div className="stats-grid">
          {stats.map((stat, index) => {
            const Icon = iconMap[stat.icon];
            return (
              <div
                key={index}
                className={`stat-card ${isVisible ? 'visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Icon size={32} className="stat-icon" />
                <div className="stat-value">
                  <AnimatedCounter value={stat.value} isVisible={isVisible} />
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            );
          })}
        </div>

        <div className="reasons-grid">
          {reasons.map((reason, index) => {
            const Icon = iconMap[reason.icon];
            return (
              <div
                key={index}
                className={`reason-card ${isVisible ? 'visible' : ''}`}
                style={{ animationDelay: `${(index + 4) * 0.1}s` }}
              >
                <div className="reason-icon">
                  <Icon size={32} />
                </div>
                <h3 className="reason-title">{reason.title}</h3>
                <p className="reason-description">{reason.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};